import { Box, Flex, SimpleGrid, Skeleton } from '@chakra-ui/react';
import FaceliftText from '@/components/Atoms/FaceliftText';
import Show from '@/components/Molecules/Show';

export type AgingBucket = {
  label: string; // e.g., '1-30 Days'
  amount: string; // formatted currency e.g., '$2,000'
};

export interface InvoiceAgingSummaryProps {
  buckets: AgingBucket[]; // expect 5 buckets in order
  isLoading?: boolean;
}

// A compact, border-boxed 5-column aging summary matching the image
export const InvoiceAgingSummary = ({ buckets, isLoading = false }: InvoiceAgingSummaryProps) => {
  const skeletonItems = Array.from({ length: 5 }, (_, i) => i);

  return (
    <Box
      borderWidth="1px"
      borderRadius="5px"
      borderColor="gray.400"
      p={4}
      _dark={{ bg: 'darkModeBgColor' }}
      _light={{ bg: 'white' }}
    >
      <Show when={isLoading}>
        <SimpleGrid columns={{ base: 1, md: 5 }} gap={{ base: 4, md: 6 }} alignItems="center">
          {skeletonItems.map((i) => (
            <Flex key={i} direction="column" align="center" gap={2} minW={{ base: '220px', md: 'auto' }}>
              <Skeleton height="16px" width="80px" _dark={{ bg: 'gray.700' }} _light={{ bg: 'gray.200' }} />
              <Skeleton height="36px" width="100px" _dark={{ bg: 'gray.700' }} _light={{ bg: 'gray.200' }} />
            </Flex>
          ))}
        </SimpleGrid>
      </Show>

      <Show when={!isLoading}>
        <SimpleGrid columns={{ base: 1, md: 5 }} gap={{ base: 4, md: 6 }} alignItems="center">
          {buckets.map((b) => (
            <Flex key={b.label} direction="column" align="center" gap={2} minW={{ base: '220px', md: 'auto' }}>
              <FaceliftText fontSize="sm" _dark={{ color: 'white' }} _light={{ color: 'gray.800' }}>
                {b.label}
              </FaceliftText>
              <Box
                as="span"
                fontSize="3xl"
                fontWeight={700}
                lineHeight="100%"
                _dark={{ color: 'white' }}
                _light={{
                  bgGradient: 'linear-gradient(91.56deg, #1C4B74 0.38%, #117964 100.38%)',
                  bgClip: 'text'
                }}
                css={{
                  _light: {
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }
                }}
              >
                {b.amount}
              </Box>
            </Flex>
          ))}
        </SimpleGrid>
      </Show>
    </Box>
  );
};

export default InvoiceAgingSummary;


